import { analyticsService } from '../index-business';

export interface RegistrationEventData {
  studioId?: string;
  groupId?: string;
  courseId?: string;
  clientId?: string;
  attendanceId?: string;
  cancellationReason?: string;
  notifyClient?: boolean;
  notifyClients?: boolean;
  source?: string;
  [key: string]: any;
}

export class RegistrationTracker {
  static trackAddClass(data: {
    studioId: string;
    registration: any;
    success: boolean;
    error?: string;
    duration?: number;
    omitClientTag?: boolean;
  }) {
    const isCreateAndConfirm = data.omitClientTag === true;

    const eventData = {
      studio_id: data.studioId,
      course_id: data.registration?.courseId,
      teacher_id: data.registration?.teacherId,
      billing_tag: data.registration?.billingTag,
      class_date: data.registration?.classDateTime,
      room_id: data.registration?.roomId,
      success: data.success,
      error_message: data.error,
      api_duration_ms: data.duration,
      event_type: 'api_call',
      api_endpoint: 'addClass',
      is_create_and_confirm: isCreateAndConfirm,
      timestamp: new Date().toISOString(),
    };

    const eventName = isCreateAndConfirm ? 'Class Created and Confirmed' : 'Class Created';

    analyticsService.track(eventName, eventData);
  }

  static trackConfirmClass(data: { registration: any; success: boolean; error?: string; duration?: number }) {
    const eventData = {
      studio_id: data.registration?.studioId,
      group_id: data.registration?.groupId,
      course_id: data.registration?.courseId,
      teacher_id: data.registration?.teacherId,
      billing_tag: data.registration?.billingTag,
      class_date: data.registration?.classDateTime,
      attendees_count: data.registration?.clients?.length || 0,
      success: data.success,
      error_message: data.error,
      api_duration_ms: data.duration,
      event_type: 'api_call',
      api_endpoint: 'confirmClass',
      timestamp: new Date().toISOString(),
    };

    analyticsService.track('Class Confirmed', eventData);
  }

  static trackCancelWholeClass(data: {
    studioId: string;
    groupId: string;
    cancellationReason: string;
    notifyClients: boolean;
    success: boolean;
    error?: string;
    duration?: number;
    clientsCount?: number;
  }) {
    const eventData = {
      studio_id: data.studioId,
      group_id: data.groupId,
      cancellation_reason: data.cancellationReason,
      notify_clients: data.notifyClients,
      affected_clients_count: data.clientsCount,
      success: data.success,
      error_message: data.error,
      api_duration_ms: data.duration,
      event_type: 'api_call',
      api_endpoint: 'cancelWholeClass',
      timestamp: new Date().toISOString(),
    };

    analyticsService.track('Whole Class Cancelled', eventData);
  }

  static trackDeleteClientFromClass(data: {
    attendanceId: string;
    cancellationReason: string;
    notifyClient: boolean;
    success: boolean;
    error?: string;
    duration?: number;
    clientId?: string;
    studioId?: string;
    groupId?: string;
  }) {
    const eventData = {
      attendance_id: data.attendanceId,
      client_id: data.clientId,
      studio_id: data.studioId,
      group_id: data.groupId,
      cancellation_reason: data.cancellationReason,
      notify_client: data.notifyClient,
      success: data.success,
      error_message: data.error,
      api_duration_ms: data.duration,
      event_type: 'api_call',
      api_endpoint: 'deleteClientToRegistration',
      timestamp: new Date().toISOString(),
    };

    analyticsService.track('Client Removed From Class', eventData);
  }

  static trackUpdateClassInfo(data: {
    groupId: string;
    updateData: any;
    success: boolean;
    error?: string;
    duration?: number;
    studioId?: string;
  }) {
    const eventData = {
      group_id: data.groupId,
      studio_id: data.studioId,
      updated_fields: data.updateData ? Object.keys(data.updateData) : [],
      class_name: data.updateData?.className,
      teacher_id: data.updateData?.teacherId,
      billing_tag: data.updateData?.billingTag,
      class_date: data.updateData?.classDateTime,
      room_id: data.updateData?.roomId,
      extra_location: data.updateData?.extraLocation,
      success: data.success,
      error_message: data.error,
      api_duration_ms: data.duration,
      event_type: 'api_call',
      api_endpoint: 'updateClassInfo',
      timestamp: new Date().toISOString(),
    };

    analyticsService.track('Class Info Updated', eventData);
  }

  static trackRegistrationApiCall(
    endpoint: string,
    data: RegistrationEventData & {
      success: boolean;
      error?: string;
      duration?: number;
    }
  ) {
    const eventData = {
      ...data,
      event_type: 'api_call',
      api_endpoint: endpoint,
      timestamp: new Date().toISOString(),
    };

    analyticsService.track(`Registration API: ${endpoint}`, eventData);
  }
}

export const trackRegistrationSuccess = (endpoint: string, data: RegistrationEventData, duration?: number) => {
  RegistrationTracker.trackRegistrationApiCall(endpoint, {
    ...data,
    success: true,
    duration,
  });
};

export const trackRegistrationError = (
  endpoint: string,
  error: string,
  data: RegistrationEventData,
  duration?: number
) => {
  RegistrationTracker.trackRegistrationApiCall(endpoint, {
    ...data,
    success: false,
    error,
    duration,
  });
};

export default RegistrationTracker;
